"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ParallelQueue = void 0;
const logger_1 = require("../../logger");
const event_1 = require("../../observing/event");
const event_late_1 = require("../../observing/event.late");
class ParallelQueue {
    constructor() {
        this._queues = {};
        this._inProgress = null;
        this._currentIndex = 0;
        this._maxIndex = 0;
        this._logger = null;
        this._beforePriorityRun = new event_1.Event();
        this._afterPriorityRun = new event_1.Event();
        this._finished = new event_late_1.OneTimeLateEvent();
        this._executeLoader = async (l, priority, index) => {
            var _a, _b;
            try {
                await l();
            }
            catch (err) {
                (_a = this._logger) === null || _a === void 0 ? void 0 : _a.warn('Failed to process queue item at priority =', priority, ' at index =', index || '?');
                (_b = this._logger) === null || _b === void 0 ? void 0 : _b.error(err);
            }
        };
    }
    get currentPriority() { return this._currentIndex; }
    get inProgress() { return this._inProgress; }
    get beforePriorityRun() { return this._beforePriorityRun.expose(); }
    get afterPriorityRun() { return this._afterPriorityRun.expose(); }
    get finished() { return this._finished.expose(); }
    withLogger(name) {
        this._logger = (0, logger_1.createLogger)(`[Queue:${name || '?'}]`);
        return this;
    }
    /** @returns A callback that will try to cancel queued item */
    enqueue(cb, priority) {
        const p = Math.round(priority || 0);
        // if it's started and the priority has been processing already
        // or it has just finished
        if (this._inProgress && p < this._currentIndex || this._inProgress === false) {
            // just start the loader
            this._executeLoader(cb, p, 1000);
            return () => false;
        }
        // Enqueue for future execution
        const q = this.getQueue(p);
        q.push(cb);
        this._maxIndex = Math.max(this._maxIndex, p);
        // Return callback for removing the item from the queue
        return () => {
            // The processing has started and this queue has been passed already
            if (this._inProgress != null && this._currentIndex >= p) {
                return false;
            }
            // find and remove the item
            const i = q.findIndex(qi => qi === cb);
            if (i >= 0) {
                q.splice(i, 1);
                // return true if we're sure that the item hasn't been executed yet
                return true;
            }
            return false;
        };
    }
    async start() {
        var _a;
        if (this._inProgress != null) {
            return undefined;
        }
        this._inProgress = true;
        try {
            await this.tryStartQueue();
        }
        catch (err) {
            (_a = this._logger) === null || _a === void 0 ? void 0 : _a.warn('Failed to process queue:', err);
            return false;
        }
        return true;
    }
    getQueue(priority = 0) {
        let q = this._queues[priority];
        if (!q) {
            q = [];
            this._queues[priority] = q;
        }
        return q;
    }
    async tryStartQueue() {
        var _a, _b, _c, _d, _e;
        let current = null;
        let iterations = 0;
        let totalItems = 0;
        await this._beforePriorityRun.triggerAsync(this._currentIndex);
        const MAX_ATTEMPTS = 5;
        while ((_a = (current = this._queues[this._currentIndex])) === null || _a === void 0 ? void 0 : _a.length) {
            if (iterations++ > MAX_ATTEMPTS) {
                (_b = this._logger) === null || _b === void 0 ? void 0 : _b.warn('Tried to purge queue for priority =', this._currentIndex, 'for too many times of', MAX_ATTEMPTS, '; totalItems =', totalItems, '; skipping.');
                break;
            }
            (_c = this._logger) === null || _c === void 0 ? void 0 : _c.log('Processing priority =', this._currentIndex, '; count =', current.length);
            const items = current.slice();
            current.length = 0;
            totalItems += items.length;
            await Promise.all(items.map((loader, index) => this._executeLoader(loader, this._currentIndex, index)));
        }
        if (!(current === null || current === void 0 ? void 0 : current.length) && iterations === 0) {
            (_d = this._logger) === null || _d === void 0 ? void 0 : _d.log('Skipping priority =', this._currentIndex, '; no items');
        }
        await this._afterPriorityRun.triggerAsync(this._currentIndex);
        const next = this._currentIndex + 1;
        if (next > this._maxIndex) {
            // looks like we've finished!
            this._inProgress = false;
            (_e = this._logger) === null || _e === void 0 ? void 0 : _e.log('Finished processing at index =', this._currentIndex);
            this._finished.trigger();
            return;
        }
        ++this._currentIndex;
        await this.tryStartQueue();
    }
}
exports.ParallelQueue = ParallelQueue;
//# sourceMappingURL=parallel.js.map